import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { tenantPermissionService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  collectPermissionSuggestions,
  matchesPermissionSearch,
  mergePermissionDetail,
  normalizePermission,
  normalizePermissionList,
  sortPermissions,
} from "../../utils/normalizePermission";
import PermissionFormDialog from "../PermissionFormDialog";
import PermissionDetailsDrawer from "../PermissionDetailsDrawer";

const PermissionManagement = () => {
  const dispatch = useDispatch();
  const { activeTenantId, tenantId } = useAuth();
  const requestTenantId = activeTenantId || tenantId;

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");

  const [selectedPermission, setSelectedPermission] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const suggestions = useMemo(
    () => collectPermissionSuggestions(permissions),
    [permissions]
  );

  const enrichPermissions = useCallback(
    async (list = []) => {
      const results = await Promise.allSettled(
        list.map((item) =>
          item?.name
            ? tenantPermissionService.getTenantPermissionByName(
                item.name,
                requestTenantId
              )
            : Promise.resolve(null)
        )
      );

      return list.map((item, index) => {
        const result = results[index];
        if (result?.status !== "fulfilled" || !result.value) return item;
        return mergePermissionDetail(item, result.value);
      });
    },
    [requestTenantId]
  );

  const loadPermissions = useCallback(async (name = appliedName, savedPermission = null) => {
    setLoading(true);
    setError("");
    try {
      const payload = await tenantPermissionService.getAllTenantPermissions(
        requestTenantId
      );
      let normalized = sortPermissions(
        normalizePermissionList(payload).filter((item) =>
          matchesPermissionSearch(item, { name })
        )
      );

      if (savedPermission) {
        normalized = sortPermissions(
          normalized.map((item) =>
            item.name === savedPermission.name ||
            item.permissionId === savedPermission.permissionId
              ? mergePermissionDetail(item, savedPermission)
              : item
          )
        );
        setPermissions(normalized);
      } else {
        setPermissions(normalized);
      }

      const enriched = sortPermissions(await enrichPermissions(normalized));
      setPermissions(
        savedPermission
          ? sortPermissions(
              enriched.map((item) =>
                item.name === savedPermission.name ||
                item.permissionId === savedPermission.permissionId
                  ? mergePermissionDetail(item, savedPermission)
                  : item
              )
            )
          : enriched
      );
    } catch (err) {
      const message = getApiErrorMessage(err);
      setPermissions([]);
      setError(message);
      dispatch(appActions.apiErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [appliedName, dispatch, enrichPermissions, requestTenantId]);

  useEffect(() => {
    loadPermissions(appliedName);
  }, [appliedName, loadPermissions]);

  const fetchPermissionDetail = useCallback(
    async (permission) => {
      const name = String(permission?.name || "");
      if (!name) return null;
      return tenantPermissionService.getTenantPermissionByName(
        name,
        requestTenantId
      );
    },
    [requestTenantId]
  );

  const openDetails = useCallback(async (row) => {
    const current = normalizePermission(row);
    setSelectedPermission(current);
    setDetailsOpen(true);

    try {
      const detail = await fetchPermissionDetail(current);
      if (detail) {
        setSelectedPermission(mergePermissionDetail(current, detail));
      }
    } catch {
      // Keep list data if get-by-name is unavailable.
    }
  }, [fetchPermissionDetail]);

  const openCreate = () => {
    setFormMode("create");
    setSelectedPermission(null);
    setFormOpen(true);
  };

  const openEdit = useCallback(async (row) => {
    if (!row) return;
    const current = normalizePermission(row);
    setSelectedPermission(current);
    setFormMode("edit");
    setDetailsOpen(false);
    setFormOpen(true);

    try {
      const detail = await fetchPermissionDetail(current);
      if (detail) {
        setSelectedPermission(mergePermissionDetail(current, detail));
      }
    } catch {
      // Keep list data if get-by-name is unavailable.
    }
  }, [fetchPermissionDetail]);

  const openDelete = useCallback((row) => {
    if (!row) return;
    setSelectedPermission(normalizePermission(row));
    setDetailsOpen(false);
    setDeleteOpen(true);
  }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (formMode === "create") {
        await tenantPermissionService.createTenantPermission(
          payload,
          requestTenantId
        );
        dispatch(appActions.apiSuccessMessage("Permission created successfully."));
        setFormOpen(false);
        await loadPermissions(appliedName);
      } else {
        await tenantPermissionService.updateTenantPermission(
          selectedPermission,
          payload,
          requestTenantId
        );
        const savedPermission = normalizePermission({
          ...selectedPermission,
          ...payload,
          updatedAt: new Date().toISOString(),
        });
        setPermissions((prev) =>
          sortPermissions(
            prev.map((item) =>
              item.name === selectedPermission.name ||
              item.permissionId === selectedPermission.permissionId ||
              item.name === savedPermission.name
                ? mergePermissionDetail(item, savedPermission)
                : item
            )
          )
        );
        dispatch(appActions.apiSuccessMessage("Permission updated successfully."));
        setFormOpen(false);
        await loadPermissions(appliedName, savedPermission);
      }
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPermission?.name) return;
    setSaving(true);
    try {
      await tenantPermissionService.deleteTenantPermission(
        selectedPermission.name,
        requestTenantId
      );
      dispatch(appActions.apiSuccessMessage("Permission deleted successfully."));
      setDeleteOpen(false);
      setDetailsOpen(false);
      await loadPermissions(appliedName);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Permission",
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "allow",
        headerName: "Allow",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.allow) ? row.allow.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.allow} maxVisible={2} />
        ),
      },
      {
        field: "deny",
        headerName: "Deny",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.deny) ? row.deny.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.deny} maxVisible={2} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 168,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <ActionIconButton
              title="View Details"
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              onClick={() => openDetails(params.row)}
            />
            <ActionIconButton
              title="Edit Permission"
              icon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => openEdit(params.row)}
            />
            <ActionIconButton
              title="Delete Permission"
              icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
              onClick={() => openDelete(params.row)}
            />
          </Stack>
        ),
      },
    ],
    [openDelete, openDetails, openEdit]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Permissions"
          subtitle="Manage tenant permissions."
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add Permission
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadPermissions(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Permission",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !permissions.length ? (
          <PageLoader text="Loading permissions..." />
        ) : !loading && !permissions.length ? (
          <EmptyState
            title="No permissions found"
            description="No permissions found for the current filters."
          />
        ) : (
          <DataTable
            rows={permissions}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => row.permissionId || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <PermissionDetailsDrawer
        open={detailsOpen}
        permission={selectedPermission}
        onClose={() => setDetailsOpen(false)}
        onEdit={() => openEdit(selectedPermission)}
        onDelete={() => openDelete(selectedPermission)}
      />

      <PermissionFormDialog
        open={formOpen}
        mode={formMode}
        permission={selectedPermission}
        suggestions={suggestions}
        loading={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete permission{" "}
            <strong>{selectedPermission?.name || selectedPermission?.permissionId}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionManagement;
